import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import type { ReactNode } from "react";
import {
  clearNavDirection,
  clearSkipStackEnter,
  getStackNavDirection,
  isStackExitActive,
  onStackExitChange,
  recordPathname,
  getAppScreenContentKey,
  shouldAnimateStackTransition,
} from "./navigationUtils";

const STACK_TRANSITION_MS = 280;

export function AppScreenContent({ children, className = "" }: { children: ReactNode; className?: string }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const key = getAppScreenContentKey(location.pathname, location.search);
  const enterDirection = getStackNavDirection(location.pathname, navigationType);
  const shouldTransition = shouldAnimateStackTransition(location.pathname, enterDirection);
  const [stackExit, setStackExit] = useState(isStackExitActive);
  const [activeTransition, setActiveTransition] = useState<"forward" | "back" | null>(
    shouldTransition ? enterDirection : null,
  );

  useEffect(() => onStackExitChange(() => {
    const exiting = isStackExitActive();
    setStackExit(exiting);
    if (exiting) setActiveTransition(null);
  }), []);

  useLayoutEffect(() => {
    recordPathname(location.pathname);

    if (document.documentElement.dataset.skipStackEnter === "true") {
      setActiveTransition(null);
      clearSkipStackEnter();
      clearNavDirection();
      return undefined;
    }

    if (shouldTransition && enterDirection) {
      setActiveTransition(enterDirection);
      const timer = window.setTimeout(() => {
        setActiveTransition(null);
        clearNavDirection();
      }, STACK_TRANSITION_MS);
      return () => window.clearTimeout(timer);
    }

    setActiveTransition(null);
    clearNavDirection();
    return undefined;
  }, [location.pathname, shouldTransition, enterDirection]);

  const transitionClass = stackExit
    ? "app-screen-transition app-screen-transition--exit-right"
    : activeTransition
      ? `app-screen-transition app-screen-transition--${activeTransition}`
      : "";

  return (
    <div className={`app-screen-content ${transitionClass} ${className}`.trim()} key={key}>
      {children}
    </div>
  );
}

export { ConnectedCelebration } from "./ConnectedCelebration";
